import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import router from '@Site/Router'
import Profile from '@Images/settings_profile.svg'
import Information from '@Images/settings_information.svg'
import Users from '@Images/account_popup_users.svg'
import Workspaces from '@Images/settings_workspaces.svg'
import Providers from '@Images/settings_providers.svg'
import Billing from '@Images/account_popup_billing.svg'
import Plan from '@Images/account_popup_plan.svg'
import Credits from '@Images/settings_credits.svg'
import Statistics from '@Images/account_popup_statistics.svg'
import LeftBorderActive from '@Images/sidebar_left_border_active.svg'

import {
  getCurrentAccountName,
  getCurrentAccountHasCurrentSubscription,
  getCurrentAccountHasEnterpriseOrCustomSubscription,
} from '@SiteModules/Account/selectors'

import {
  createCustomerPortalSession,
} from '@SiteModules/User/actions'

import './SettingsMenu.scss'

interface SettingsMenuProps {
  readonly activeSettings: string
  readonly userIsAccountAdmin: boolean
  readonly currentAccountId: number
  setActiveSettings(newActiveSettings: string): void
}

const SettingsMenu: React.FC<SettingsMenuProps> = (props) => {
  const dispatch = useDispatch()

  const {
    activeSettings,
    userIsAccountAdmin,
    currentAccountId,
  } = props

  const currentAccountName = useSelector(getCurrentAccountName)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)
  const currentAccountHasEnterpriseOrCustomSubscription = useSelector(getCurrentAccountHasEnterpriseOrCustomSubscription)
  const currentAccountIsSet = currentAccountId >= 0

  const userProfileSettingsActive = activeSettings === 'profile'
  const accountInformationSettingsActive = activeSettings === 'information'
  const accountUsersSettingsActive = activeSettings === 'users'
  const accountWorkspacesSettingsActive = activeSettings === 'workspaces'
  const accountProviderSettingsActive = activeSettings === 'providers'
  const accountCreditsPurchaseActive = activeSettings === 'credits'
  const statisticsActive = activeSettings === 'statistics'

  const shouldRenderBillingAndPlan = !currentAccountHasEnterpriseOrCustomSubscription || !currentAccountHasCurrentSubscription
  const shouldRenderCredits = currentAccountHasCurrentSubscription

  const setUserProfileSettingsActive = () => {
    props.setActiveSettings('profile')
  }

  const setAccountInformationSettingsActive = () => {
    props.setActiveSettings('information')
  }

  const setAccountUsersSettingsActive = () => {
    props.setActiveSettings('users')
  }

  const setAccountWorkspacesSettingsActive = () => {
    props.setActiveSettings('workspaces')
  }

  const setAccountProviderSettingsActive = () => {
    props.setActiveSettings('providers')
  }

  const goToSubscriptionPage = () => {
    router.navigate('/subscriptions')
  }

  const goToBilling = () => {
    dispatch(createCustomerPortalSession({ accountId: currentAccountId }))
  }

  const setAccountCreditsPurchaseActive = () => {
    props.setActiveSettings('credits')
  }

  const setStatisticsActive = () => {
    props.setActiveSettings('statistics')
  }

  const renderPersonalSection = () => {
    return (
      <div className="SettingsMenu__section SettingsMenu__section-personal">
        <div className="SettingsMenu__section-header">
          Personal
        </div>
        <ul className="SettingsMenu__items">
          <li
            className={classnames(
              'SettingsMenu__item',
              { 'SettingsMenu__item-active': userProfileSettingsActive }
            )}
            onClick={setUserProfileSettingsActive}
          >
            <Profile />
            <div>
              Profile
            </div>
            {userProfileSettingsActive && renderLeftBorderActive()}
          </li>
        </ul>
      </div>
    )
  }

  const renderAccountSection = () => {
    return (
      <div className="SettingsMenu__section SettingsMenu__section-account">
        <div className="SettingsMenu__section-header">
          {currentAccountName}
        </div>
        <ul className="SettingsMenu__items">
          {userIsAccountAdmin && renderAccountSectionItemsAdmin()}
          <li
            className={classnames(
              'SettingsMenu__item',
              { 'SettingsMenu__item-active': statisticsActive }
            )}
            onClick={setStatisticsActive}
          >
            <Statistics />
            <div>
              Statistics
            </div>
            {statisticsActive && renderLeftBorderActive()}
          </li>
        </ul>
      </div>
    )
  }

  const renderAccountSectionItemsAdmin = () => {
    return (
      <React.Fragment>
        <li
          className={classnames(
            'SettingsMenu__item',
            { 'SettingsMenu__item-active': accountInformationSettingsActive }
          )}
          onClick={setAccountInformationSettingsActive}
        >
          <Information />
          <div>
            Information
          </div>
          {accountInformationSettingsActive && renderLeftBorderActive()}
        </li>
        <li
          className={classnames(
            'SettingsMenu__item',
            { 'SettingsMenu__item-active': accountUsersSettingsActive }
          )}
          onClick={setAccountUsersSettingsActive}
        >
          <Users />
          <div>
            Users
          </div>
          {accountUsersSettingsActive && renderLeftBorderActive()}
        </li>
        <li
          className={classnames(
            'SettingsMenu__item',
            { 'SettingsMenu__item-active': accountWorkspacesSettingsActive }
          )}
          onClick={setAccountWorkspacesSettingsActive}
        >
          <Workspaces />
          <div>
            Workspaces
          </div>
          {accountWorkspacesSettingsActive && renderLeftBorderActive()}
        </li>
        <li
          className={classnames(
            'SettingsMenu__item',
            { 'SettingsMenu__item-active': accountProviderSettingsActive }
          )}
          onClick={setAccountProviderSettingsActive}
        >
          <Providers />
          <div>
            Providers
          </div>
          {accountProviderSettingsActive && renderLeftBorderActive()}
        </li>
        {shouldRenderBillingAndPlan && (
          <li
            className="SettingsMenu__item"
            onClick={goToSubscriptionPage}
          >
            <Plan />
            <div>
              Plan
            </div>
          </li>
        )}
        {shouldRenderBillingAndPlan && (
          <li
            className="SettingsMenu__item"
            onClick={goToBilling}
          >
            <Billing />
            <div>
              Billing
            </div>
          </li>
        )}
        {shouldRenderCredits && (
          <li
            className={classnames(
              'SettingsMenu__item',
              { 'SettingsMenu__item-active': accountCreditsPurchaseActive }
            )}
            onClick={setAccountCreditsPurchaseActive}
          >
            <Credits />
            <div>
              Credits
            </div>
            {accountCreditsPurchaseActive && renderLeftBorderActive()}
          </li>
        )}
      </React.Fragment>
    )
  }

  const renderLeftBorderActive = () => {
    return (
      <div className="SettingsMenu__item-left-border-active">
        <LeftBorderActive />
      </div>
    )
  }

  return (
    <div className="SettingsMenu">
      {renderPersonalSection()}
      {currentAccountIsSet && renderAccountSection()}
    </div>
  )
}

export default SettingsMenu
